import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { PRODUCT_KEY_MAP } from "../translations-products";
import {
  TrendingUp, CheckCircle, ChevronDown, ChevronRight, ArrowRight,
  Star, Shield, Globe, MapPin, Zap, Search, Eye
} from "lucide-react";

export default function ProductPage() {
  const { productId, region, city } = useParams();
  const { t } = useLanguage();
  const [product, setProduct] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const endpoint = region
      ? `${API}/products/${productId}/local/${region}`
      : `${API}/products/${productId}`;
    axios.get(endpoint).then(r => setProduct(r.data)).catch(() => {});
  }, [productId, region]);

  if (!product) return (
    <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#a855f7] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Translation helper — get translated product field or fallback to backend data
  const keyPrefix = PRODUCT_KEY_MAP[productId] || "";
  const pName = t[`pp_${keyPrefix}_name`] || product.name;
  const pTagline = t[`pp_${keyPrefix}_tag`] || product.tagline;
  const pDesc = t[`pp_${keyPrefix}_desc`] || product.description;

  // Translate features — try pp_{prefix}_f1, f2, etc., fallback to backend
  const features = (product.features || []).map((f, i) => t[`pp_${keyPrefix}_f${i + 1}`] || f);

  // Translate price label
  const priceLabel = product.price_value > 0 ? product.price
    : product.price === "Included in all plans" ? (t.pp_included || product.price)
    : product.price === "Free first scan" ? (t.pp_free_scan || product.price)
    : product.price === "Free" ? (t.pp_free || product.price)
    : product.price;

  const localLabel = product.region_name ? ` — ${product.region_name}${city ? ` / ${city}` : ''}` : '';

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="hidden sm:flex items-center gap-4">
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.pp_home || "Home"}</a>
            <a href="/ai-simulator" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_simulator || "Simulator"}</a>
            <a href="/plugin" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">Plugin</a>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-6 py-4">
        <nav className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] font-['JetBrains_Mono']" data-testid="product-breadcrumb">
          <a href="/" className="hover:text-[var(--color-text)]">{t.pp_home || "Home"}</a>
          <ChevronRight className="w-3 h-3" />
          <a href={`/products/${productId}`} className="hover:text-[var(--color-text)]">{pName}</a>
          {product.region_name && <>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[var(--color-text)]">{product.region_name}</span>
          </>}
        </nav>
      </div>

      {/* Hero */}
      <section className="py-16 px-6" data-testid="product-hero">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            {product.price_value > 0 ? (
              <span className="px-3 py-1 bg-[#FFE600]/10 text-[#FFE600] text-sm font-bold">{priceLabel}</span>
            ) : (
              <span className="px-3 py-1 bg-emerald-900/30 text-emerald-400 text-sm font-bold">{priceLabel}</span>
            )}
            {localLabel && <span className="px-3 py-1 bg-[var(--color-surface-alt)] text-[var(--color-text-muted)] text-xs"><MapPin className="w-3 h-3 inline mr-1" />{product.region_name}</span>}
          </div>
          <h1 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-4" data-testid="product-title">
            {pName}{localLabel}
          </h1>
          <p className="text-xl text-[var(--color-text-muted)] mb-6 max-w-2xl">{pTagline}</p>
          <p className="text-[var(--color-text-muted)] mb-8 max-w-3xl leading-relaxed">{pDesc}</p>
          <div className="flex flex-wrap gap-4">
            <a href="/login" className="px-8 py-4 bg-[#a855f7] text-white font-black text-lg uppercase tracking-wider hover:bg-purple-600 transition-all" data-testid="product-cta">
              <Search className="w-5 h-5 inline mr-2" />
              {product.price_value > 0 ? (t.pp_start_trial || "Start Free Trial") : (t.pp_try_free || "TRY FREE NOW")}
            </a>
            {product.id === 'wordpress-plugin' && (
              <a href="/plugin" className="px-8 py-4 bg-[var(--color-surface)] border border-[var(--color-border)] text-white font-bold text-lg hover:border-zinc-500 transition-all">{t.pp_download_plugin || "Download Plugin"}</a>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]" data-testid="product-features">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-8">{t.pp_whats_included || "WHAT'S INCLUDED"}</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {features.map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-[var(--color-bg)] border border-[var(--color-border)]">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-[var(--color-text)] text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      {product.reviews?.length > 0 && (
        <section className="py-16 px-6" data-testid="product-reviews">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-8">{t.pp_what_clients_say || "WHAT CLIENTS SAY"}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {product.reviews.map((r, i) => (
                <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid={`product-review-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-[#FFE600] fill-[#FFE600]" />)}</div>
                    <span className="text-xs font-bold px-2 py-1 bg-emerald-900/30 text-emerald-400 font-['JetBrains_Mono']">{r.boost}</span>
                  </div>
                  <p className="text-[var(--color-text)] text-sm leading-relaxed mb-4">"{r.text}"</p>
                  <p className="text-[var(--color-text-muted)] text-xs">{r.name} — {r.role}, {r.country}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {product.faq?.length > 0 && (
        <section className="py-16 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]" data-testid="product-faq">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-8 text-center">FAQ</h2>
            {product.faq.map((item, i) => (
              <div key={i} className="border-b border-[var(--color-border)]" data-testid={`product-faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between py-5 text-left">
                  <span className="font-semibold text-[var(--color-text)] text-sm">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[var(--color-text-muted)] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="text-[var(--color-text-muted)] text-sm pb-5 leading-relaxed">{item.a}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Local Cities */}
      {product.cities?.length > 0 && (
        <section className="py-16 px-6" data-testid="product-cities">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-6 text-center">
              {pName} — {product.region_name}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {product.cities.map((c, i) => {
                const slug = c.toLowerCase().replace(/ /g, "-");
                return (
                  <Link key={i} to={`/seo/${region}/${slug}`}
                    className="bg-[var(--color-surface)] border border-[var(--color-border)] p-3 text-center hover:border-[#a855f7] transition-colors text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                    <MapPin className="w-3 h-3 mx-auto mb-1 text-[var(--color-text-muted)]" />{c}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-6 bg-[var(--color-bg)]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            {t.pp_ready || "READY TO"} <span className="text-[#a855f7]">{t.pp_start || "START"}</span>?
          </h2>
          <p className="text-[var(--color-text-muted)] mb-8">{t.pp_join || "Join thousands of businesses using TopRankPulse."}</p>
          <a href="/login" className="inline-flex items-center gap-3 px-10 py-5 bg-[#a855f7] text-white font-black text-xl uppercase tracking-wider hover:bg-purple-600 transition-all" data-testid="product-cta-bottom">
            <Zap className="w-6 h-6" /> {t.pp_get_started || "GET STARTED FREE"}
          </a>
        </div>
      </section>

      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
